import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../contexts/AuthContext";
import { categoryService } from "../../../services/api/categoryService";
import { firebaseService } from "../../../services/api/firebaseService";
import CustomTable from "../components/CustomTable";
import SearchAndFilterBar from "../components/SearchAndFilterBar";
import AddCategory from "./AddCategory";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { htmlToText } from "../../../utils/htmlUtils";
import useDebouncedSearch from "../../../hooks/useDebouncedSearch";

const ListCategory = () => {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search") || "";
  const currentPage = Number(searchParams.get("page")) || 1;

  const [popupMode, setPopupMode] = useState("add"); // "add" | "edit"
  const [editingCategory, setEditingCategory] = useState(null);

  const [searchValue, setSearchValue] = useState(search);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null); // Changed from deleteId to deleteItem to store more info

  const [data, setData] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPage, setTotalPage] = useState(1);

  /** Extract Firebase image URLs from HTML content */
  const extractFirebaseImageUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");
    const firebaseUrls = [];
    images.forEach((img) => {
      const src = img.getAttribute("src");
      if (src && (src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/"))) {
        firebaseUrls.push(src);
      }
    });
    return firebaseUrls;
  };

  const formatData = useCallback(
    (responseData) =>
      responseData.map((item, index) => ({
        id: itemsPerPage * (currentPage - 1) + index + 1,
        logo: item.image || "/images/logo.png",
        categoryName: item.name || "Chưa có tên",
        detail: htmlToText(item.description, "Không có mô tả"),
        nav_id: item.id || null,
        originalData: item, // Keep original data for deletion cleanup
      })),
    [currentPage, itemsPerPage],
  );

  const fetchData = useCallback(async () => {
    try {
      setTableLoading(true);
      const response = await categoryService.get_all(
        currentPage,
        itemsPerPage,
        search,
        token,
      );
      setData(formatData(response.categories));
      setTotalPage(response.totalPages || 1);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setTableLoading(false);
    }
  }, [token, currentPage, itemsPerPage, search, formatData]);

  const confirmDelete = async () => {
    if (!deleteItem?.nav_id) return;

    try {
      setDeleteLoading(true);

      // 1. Get all images associated with this category
      const logoUrl = deleteItem.originalData?.image;
      const descriptionHtml = deleteItem.originalData?.description || "";
      const contentImages = extractFirebaseImageUrls(descriptionHtml);

      const allImagesToDelete = [...contentImages];
      if (logoUrl) allImagesToDelete.push(logoUrl);

      // 2. Delete from database
      await categoryService.delete_category(deleteItem.nav_id, token);

      // 3. Cleanup all images from Firebase
      for (const url of allImagesToDelete) {
        if (url.startsWith("https://storage.googleapis.com/") || url.startsWith("https://firebasestorage.googleapis.com/")) {
          try {
            await firebaseService.delete_file(token, url);
          } catch (err) {
            console.warn("Failed to delete image from Firebase:", url, err);
          }
        }
      }

      toast.success("Xóa loại dịch vụ thành công!");
      await fetchData();
    } catch (error) {
      console.log("🚀 ~ confirmDelete ~ error:", error);
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setDeleteLoading(false);
      setDeleteItem(null);
    }
  };

  const applySearch = (value) => {
    const params = new URLSearchParams();
    if (value) params.set("search", value);
    params.set("page", "1");
    setSearchParams(params);
  };
  const { debouncedSearch, searchImmediately } = useDebouncedSearch(
    applySearch,
    500,
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    debouncedSearch(value);
  };

  useEffect(() => {
    fetchData();
    if (user?.role === "SuperAdmin") setCanDelete(true);
  }, [fetchData, user?.role]);

  const columns = [
    {
      key: "id",
      title: "STT",
      minWidth: "20px",
      align: "text-left",
      render: (value) => <div className="pl-2">{value}</div>,
    },
    {
      key: "logo",
      title: "Hình ảnh loại dịch vụ",
      render: (value) => (
        <img
          src={value}
          alt="Logo"
          className="size-[5.5rem] rounded-xl object-contain"
        />
      ),
    },
    {
      key: "categoryName",
      title: "Tên loại dịch vụ",
      render: (value) => (
        <div className="text-[#0267FF] no-underline min-w-[50px]">{value}</div>
      ),
    },
    {
      key: "detail",
      title: "Mô tả",
      render: (value) => <div className="min-w-[500px]">{value}</div>,
    },
  ];

  return (
    <div className="flex flex-col h-full min-h-0 gap-8 relative">
      {/* Header/Search: cố định */}
      <div className="shrink-0">
        <SearchAndFilterBar
          searchPlaceholder="Tìm kiếm loại dịch vụ"
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          onSearchClick={(value = searchValue) => searchImmediately(value)}
          showDatePickers={false}
          showFilter={false}
          onAddClick={() => {
            setPopupMode("add");
            setEditingCategory(null);
            setIsPopupOpen(true);
          }}
          addButtonLabel="Thêm dịch vụ"
          addButtonIcon="/icons/fi-plus-circle.svg"
          widthButton="152px"
        />
      </div>

      {/* ===== TABLE WRAPPER: outer visible, inner scroll + buffer ===== */}
      <div className="flex-1 min-h-0 overflow-visible">
        <div className="h-full min-h-0 overflow-auto p-[2px]">
          <CustomTable
            data={data}
            columns={columns}
            currentPage={currentPage}
            setCurrentPage={(page) => {
              const p = new URLSearchParams(searchParams);
              p.set("page", page.toString());
              setSearchParams(p);
            }}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalPages={totalPage}
            onRowClick={(item) => {
              if (item.nav_id) navigate("/loai-dich-vu/" + item.nav_id);
            }}
            setView={data.length > 0}
            setEdit={data.length > 0}
            handleEdit={(item) => {
              setPopupMode("edit");
              setEditingCategory(item);
              setIsPopupOpen(true);
            }}
            handleDelete={(item) => setDeleteItem(item)}
            setDelete={canDelete}
            basePath="/loai-dich-vu"
            loading={tableLoading}
            loadingText="Đang tải danh sách loại dịch vụ..."
          />
        </div>
      </div>

      {/* Modal confirm delete */}
      <ConfirmDeleteModal
        isOpen={!!deleteItem}
        onClose={() => (deleteLoading ? null : setDeleteItem(null))}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title="Xác nhận xóa"
        message="Bạn có chắc chắn muốn xóa loại dịch vụ này không?"
      />

      <AddCategory
        token={token}
        isOpen={isPopupOpen}
        editId={editingCategory}
        mode={popupMode}
        onClose={() => {
          setIsPopupOpen(false);
          setPopupMode("add");
          setEditingCategory(null);
        }}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default ListCategory;
